import fp from 'lodash/fp';
import { useState, useCallback, useMemo } from 'react';
import { Common } from '@factorin/domain';

import { localStorageAccessorJSON, BrowserStorageAccessors, usePrevious, useUpdateOnlyEffect } from 'Modules/ui';

import { FactorinGridColumn } from '../common';


type Ratio = number;

type Ratios = Common.Catalog<Ratio>;

const mapRatios = fp.map<FactorinGridColumn, Ratio>(({ ratio, fixedWidth }) =>
    fixedWidth ? 0 : fp.defaultTo(1, ratio));

const normalizeColumnRatios = (columns: FactorinGridColumn[]): Ratios => {
    const widths = mapRatios(columns);
    const multiplier = 1 / fp.sum(widths);
    return columns.reduce<Ratios>((result, { field }, index) => {
        result[field] = fp.multiply(multiplier, widths[index]);
        return result;
    }, {});
};

const getInitialRatios = (
    columns: FactorinGridColumn[],
    storage: BrowserStorageAccessors<Ratios>,
): Ratios => {
    if (storage) {
        const ratios = storage.get();
        if (ratios && fp.all(({ field }) => !!ratios[field], columns))
            return ratios;
    }
    return normalizeColumnRatios(columns);
};

const calculateRatios = (
    columns: FactorinGridColumn[],
    widths: number[],
    containerWidth: number,
): Ratios => {
    let sum = 0;
    let fixedSum = 0;
    for (let i = 0; i < columns.length; i++) {
        if (columns[i].fixedWidth)
            fixedSum += widths[i];
        sum += widths[i];
    }
    const totalWidth = Math.min(sum - fixedSum, (containerWidth - 1) * (1 - fixedSum / sum));
    return columns.reduce<Ratios>((ratios, { fixedWidth, field }, index) => {
        ratios[field] = fixedWidth ? 0 : +(widths[index] / totalWidth).toFixed(4);
        return ratios;
    }, {});
};

const getSumColumnRatios = fp.pipe(
    mapRatios,
    fp.sum,
);

type UpdateRatios = (widths: number[], scrollLeft: number) => void;

export type UseRatiosResult = {
    ratios: Ratios;
    updateRatios: UpdateRatios;
    resetRatios: () => void;
}

export const useRatios = (
    columns: FactorinGridColumn[],
    storageKey: string,
    onResize: (scrollLeft: number) => void,
    containerWidth: number,
): UseRatiosResult => {
    const storage = useMemo(
        () => storageKey ? localStorageAccessorJSON<Ratios>(storageKey) : null,
        [storageKey],
    );

    const [ratios, setRatios] = useState(() => getInitialRatios(columns, storage));

    const setRatiosCallback = useCallback(
        (ratios: Ratios, scrollLeft: number) => {
            setRatios(ratios);
            if (storage)
                storage.set(ratios);

            onResize(scrollLeft);
        },
        [storage, onResize],
    );

    const updateRatios = useCallback<UpdateRatios>(
        (widths, scrollLeft) => {
            const ratios = calculateRatios(columns, widths, containerWidth);
            setRatiosCallback(ratios, scrollLeft);
        },
        [columns, containerWidth, setRatiosCallback],
    );

    const resetRatios = useCallback(
        () => setRatiosCallback(normalizeColumnRatios(columns), 0),
        [columns, setRatiosCallback],
    );

    const prevColumns = usePrevious(columns);
    useUpdateOnlyEffect(
        () => {
            if (prevColumns.length !== columns.length) {
                const oldRatios = getSumColumnRatios(prevColumns);
                const newRatios = getSumColumnRatios(columns);
                const multiplier = oldRatios / newRatios;

                setRatios(ratios => {
                    const newRatios = columns.reduce<Ratios>((result, { field }) => {
                        result[field] = ratios[field]
                            ? ratios[field] * multiplier
                            : 1 - multiplier;
                        return result;
                    }, {});

                    if (storage)
                        storage.set(newRatios);

                    return newRatios;
                });

                onResize(0);
            }
        },
        [columns, prevColumns, storage, onResize],
    );

    return {
        ratios,
        updateRatios,
        resetRatios,
    };
};
